import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import CalculateIcon from '@mui/icons-material/Calculate';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API } from '../config/apiConfig';
import DashboardPageHeader from '../components/DashboardPageHeader';
import { portal, portalGradientTextSx } from '../styles/dashboardBrand';
import {
  EARNINGS_MAX_LEVELS,
  computeEarningsEstimate,
  formatUsd,
  levelCountsArrayFromRows,
  maxMembersForLevel,
  clampLevelCounts,
  fillLevelCountsForTarget,
} from '../utils/earningsCalculator';

const DISCLAIMER = `This calculator provides illustrative estimates only. Figures are based on the last available MLM period ad prices, your current community downline structure, and assumptions of 6 ads per day at 100% activity. Actual earnings depend on real ad revenue, member activity, eligibility rules, and monthly close processing. This is not a guarantee of future income.`;

function EarningsCalculator() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [periodTab, setPeriodTab] = useState('month');
  const [levelCounts, setLevelCounts] = useState(Array(EARNINGS_MAX_LEVELS).fill(0));
  const [inputs, setInputs] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [saving, setSaving] = useState(false);
  const [levelsEdited, setLevelsEdited] = useState(false);
  const [targetInput, setTargetInput] = useState('');
  const [targetSummary, setTargetSummary] = useState(null);

  const loadData = useCallback(async (uid) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in again.');
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    setError(null);
    try {
      const [calcRes, scenRes] = await Promise.all([
        axios.get(API.earningsCalculator(uid), { headers }),
        axios.get(API.earningsCalculatorScenarios(uid), { headers }),
      ]);
      if (!calcRes.data?.success) throw new Error(calcRes.data?.error || 'Failed to load calculator');
      const calc = calcRes.data.calculator;
      setInputs(calc);
      setLevelCounts(clampLevelCounts(levelCountsArrayFromRows(calc.levelCounts)));
      setScenarios(scenRes.data?.success ? scenRes.data.scenarios : []);
      setTargetSummary(null);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || err.message || 'Failed to load calculator');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in again.');
      setLoading(false);
      return;
    }
    const decoded = jwtDecode(token);
    const uid = decoded.UserID || decoded.userId;
    setUserId(uid);
    loadData(uid);
  }, [loadData]);

  const estimate = useMemo(() => {
    if (!inputs) return null;
    return computeEarningsEstimate({
      levelCounts,
      levelCountsByCountry: inputs.levelCountsByCountry,
      countryPrices: inputs.countryPrices,
      globalAvgGrossPerAdUsd: inputs.globalAvgGrossPerAdUsd,
      userCountryId: inputs.user?.countryOfResidenceId,
      periodTab,
      useUserCountryForAllLevels: levelsEdited,
    });
  }, [inputs, levelCounts, periodTab, levelsEdited]);

  const handleLevelChange = (level, value) => {
    const max = maxMembersForLevel(level);
    const n = Math.min(max, Math.max(0, parseInt(value, 10) || 0));
    setLevelsEdited(true);
    setTargetSummary(null);
    setLevelCounts((prev) => {
      const next = [...prev];
      next[level - 1] = n;
      return next;
    });
  };

  const handleCalculateTarget = () => {
    if (!inputs) return;
    const raw = String(targetInput).replace(/[^0-9.]/g, '');
    const targetUsd = parseFloat(raw);
    if (!Number.isFinite(targetUsd) || targetUsd < 0) {
      setError('Enter a valid earning target in USD.');
      return;
    }
    const result = fillLevelCountsForTarget({
      targetUsd,
      inputs,
      periodTab,
    });
    setLevelCounts(result.levelCounts);
    setLevelsEdited(true);
    setTargetSummary(result);
  };

  const handleSaveScenario = async () => {
    if (!userId || !scenarioName.trim()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        API.earningsCalculatorScenarios(userId),
        {
          scenarioName: scenarioName.trim(),
          periodTab,
          levelCounts: Object.fromEntries(levelCounts.map((c, i) => [String(i + 1), c])),
        },
        { headers }
      );
      const scenRes = await axios.get(API.earningsCalculatorScenarios(userId), { headers });
      setScenarios(scenRes.data?.success ? scenRes.data.scenarios : []);
      setSaveOpen(false);
      setScenarioName('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save scenario');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadScenario = (scenario) => {
    const arr = Array(EARNINGS_MAX_LEVELS).fill(0);
    const src = scenario.levelCounts || {};
    for (let i = 1; i <= EARNINGS_MAX_LEVELS; i += 1) {
      arr[i - 1] = Math.max(0, Number(src[String(i)] ?? src[i]) || 0);
    }
    setLevelCounts(clampLevelCounts(arr));
    setPeriodTab(scenario.periodTab || 'month');
    setLevelsEdited(true);
    setTargetSummary(null);
  };

  const handleDeleteScenario = async (scenarioId) => {
    if (!userId) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(API.earningsCalculatorScenario(userId, scenarioId), { headers });
      setScenarios((prev) => prev.filter((s) => s.scenarioId !== scenarioId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete scenario');
    }
  };

  const periodLabel = periodTab === 'day' ? 'Daily' : periodTab === 'week' ? 'Weekly' : 'Monthly';

  return (
    <Box>
      <DashboardPageHeader
        title="Earnings Calculator"
        subtitle="Estimate your potential earnings from ads and referrals"
        icon={CalculateIcon}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {inputs?.period?.priceWarning ? (
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              {inputs.period.priceWarning}
            </Alert>
          ) : null}

          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Price period
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {inputs?.period?.periodStart && inputs?.period?.periodEnd
                ? `${inputs.period.periodStart} → ${inputs.period.periodEnd}`
                : 'No period data'}
              {inputs?.period?.isClosedPeriod ? (
                <Chip label="Closed period" size="small" color="success" sx={{ ml: 1 }} />
              ) : (
                <Chip label="Latest known" size="small" sx={{ ml: 1 }} />
              )}
            </Typography>
            {inputs?.user?.countryName ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Your country: {inputs.user.countryName}
              </Typography>
            ) : null}
          </Paper>

          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
            <Tabs
              value={periodTab}
              onChange={(_, v) => setPeriodTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Daily" value="day" />
              <Tab label="Weekly" value="week" />
              <Tab label="Monthly" value="month" />
            </Tabs>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Own ads ({periodLabel})
                </Typography>
                <Typography variant="h5" sx={portalGradientTextSx({ fontWeight: 800 })}>
                  {formatUsd(estimate?.ownUsd)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Referral bonuses ({periodLabel})
                </Typography>
                <Typography variant="h5" sx={portalGradientTextSx({ fontWeight: 800 })}>
                  {formatUsd(estimate?.referralUsd)}
                </Typography>
                {estimate?.referralCapped ? (
                  <Typography variant="caption" color="warning.main">
                    Capped at {formatUsd(estimate.referralCapUsd)} (raw {formatUsd(estimate.referralRawUsd)})
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Total ({periodLabel})
                </Typography>
                <Typography variant="h4" sx={portalGradientTextSx({ fontWeight: 900 })}>
                  {formatUsd(estimate?.totalUsd)}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Earning target
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Enter a {periodLabel.toLowerCase()} USD target. We fill L1→L18 (max 2, 4, 8…) until the estimate reaches it.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }} sx={{ mb: 2 }}>
              <TextField
                label="Earning target (USD)"
                type="text"
                size="small"
                fullWidth
                value={targetInput}
                onChange={(e) => setTargetInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCalculateTarget();
                }}
                placeholder="e.g. 100"
                inputProps={{ inputMode: 'decimal' }}
                sx={{ maxWidth: { sm: 280 } }}
              />
              <Button variant="contained" onClick={handleCalculateTarget} sx={{ whiteSpace: 'nowrap' }}>
                Calculate levels
              </Button>
            </Stack>

            {targetSummary ? (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Target summary
                </Typography>
                <Typography variant="body2">
                  Target: <strong>{formatUsd(targetSummary.targetUsd)}</strong>
                  {' · '}
                  Estimated total: <strong>{formatUsd(estimate?.totalUsd ?? targetSummary.estimate?.totalUsd)}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Total people required: <strong>{targetSummary.totalPeople}</strong>
                </Typography>
                {targetSummary.ownMeetsTarget ? (
                  <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                    Your own ad earnings already meet this target — no extra members needed.
                  </Typography>
                ) : null}
                {!targetSummary.reached && targetSummary.shortfallUsd > 0 ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Full L1–L18 tree still short by {formatUsd(targetSummary.shortfallUsd)}
                    {estimate?.referralCapped ? ' (referral bonus may be capped).' : '.'}
                  </Alert>
                ) : null}
                {targetSummary.totalPeople > 0 ? (
                  <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.5 }}>
                    {targetSummary.levelCounts.map((count, i) =>
                      count > 0 ? (
                        <Chip key={`sum-L${i + 1}`} size="small" label={`L${i + 1}: ${count}`} variant="outlined" />
                      ) : null
                    )}
                  </Stack>
                ) : null}
              </Box>
            ) : null}

            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Community downline levels (L1–L18)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Edit counts to model growth (each level capped at 2^level). Hypothetical members use your country ad price.
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
                gap: 1.5,
              }}
            >
              {Array.from({ length: EARNINGS_MAX_LEVELS }, (_, i) => i + 1).map((level) => {
                const max = maxMembersForLevel(level);
                return (
                  <TextField
                    key={level}
                    label={`L${level}`}
                    type="number"
                    size="small"
                    inputProps={{ min: 0, max, step: 1 }}
                    value={levelCounts[level - 1]}
                    onChange={(e) => handleLevelChange(level, e.target.value)}
                    helperText={`Max ${max}`}
                  />
                );
              })}
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => setSaveOpen(true)}
                disabled={scenarios.length >= 10}
              >
                Save scenario
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setLevelCounts(clampLevelCounts(levelCountsArrayFromRows(inputs?.levelCounts)));
                  setLevelsEdited(false);
                  setTargetSummary(null);
                }}
              >
                Reset to my network
              </Button>
            </Stack>
          </Paper>

          {scenarios.length > 0 ? (
            <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Saved scenarios ({scenarios.length}/10)
              </Typography>
              <Stack spacing={1}>
                {scenarios.map((s) => (
                  <Stack
                    key={s.scenarioId}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Button size="small" onClick={() => handleLoadScenario(s)}>
                      {s.scenarioName}
                    </Button>
                    <Chip label={s.periodTab} size="small" variant="outlined" />
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDeleteScenario(s.scenarioId)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          ) : null}

          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Disclaimer
            </Typography>
            <Typography variant="body2">{DISCLAIMER}</Typography>
          </Alert>
        </Stack>
      )}

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save scenario</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Scenario name"
            value={scenarioName}
            onChange={(e) => setScenarioName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveScenario} variant="contained" disabled={saving || !scenarioName.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EarningsCalculator;
